import { useSSOStatus } from 'api/configurations';
import { useMethodGuide } from 'api/guide';
import { ROUTES, RouteSegments } from 'constants/routes';
import { GENERIC_IDP_ID, GuideType, Method, SCIMMethod } from 'consts';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import { useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useNavigate, useSearchParams } from 'react-router-dom';
import { env } from 'app/Env/env';
import { useInternalRouter } from 'contexts/InternalRouterCtx/InternalRouterCtx';
import { QueryParamK } from 'keys';
import { FeatureFlagId } from 'types/featureflags';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { MAPPING_CONTENT_COMP } from './string';
import { fillSegments, filterSearchParams, SegmentValues } from './routeUtils';

const searchParamsToPreserve = [
	QueryParamK.TenantId,
	QueryParamK.SsoId,
	QueryParamK.CookieName
];

export const useNav = () => {
	const navigate = useNavigate();
	const internal = useInternalRouter();
	const [searchParams] = useSearchParams();
	const { isFeatureFlagActive } = useFeatureFlags();

	const preserveQueryParamsEnabled = isFeatureFlagActive(
		FeatureFlagId.PRESERVE_QUERY_PARAMS
	);

	const nav = useCallback(
		(route: string) => {
			let search = '';

			if (preserveQueryParamsEnabled) {
				const preservedSearchParams = filterSearchParams(
					searchParams,
					searchParamsToPreserve
				);
				search =
					preservedSearchParams.size > 0
						? `?${preservedSearchParams.toString()}`
						: '';
			}
			/* istanbul ignore if: need to render under external router to test this */
			if (!internal) {
				// router is not internal to the sso suite. and so we have to append the baseurl by ourselves
				navigate(`${env.baseRoute || ''}${route}${search}`, {
					replace: true
				});
				return;
			}
			navigate(`${route}${search}`);
		},
		[internal, navigate, searchParams, preserveQueryParamsEnabled]
	);

	return nav;
};

export const useSyncIdPConfigContextToSegments = () => {
	const {
		setGuideType,
		setIdPID,
		setMethod,
		setGuideStepNum,
		setScimIdPID,
		setScimMethod,
		setScimGuideStepNum,
		setXaaIdPID,
		setXaaMethod,
		setXaaGuideStepNum
	} = useIdPConfigContext();

	const syncIdPConfigContextToSegments = useCallback(
		(segmentsValues: SegmentValues) => {
			const guideType = segmentsValues[RouteSegments.Guide] as GuideType;

			if (!guideType) return;

			setGuideType(guideType);
			if (guideType === GuideType.SSO) {
				if (segmentsValues[RouteSegments.IdPID]) {
					setIdPID(segmentsValues[RouteSegments.IdPID] as string);
				}
				if (segmentsValues[RouteSegments.Method]) {
					setMethod(segmentsValues[RouteSegments.Method] as Method);
				}
				if (segmentsValues[RouteSegments.StepNum]) {
					setGuideStepNum(segmentsValues[RouteSegments.StepNum] as number);
				}
			}
			if (guideType === GuideType.SCIM) {
				if (segmentsValues[RouteSegments.IdPID]) {
					setScimIdPID(segmentsValues[RouteSegments.IdPID] as string);
				}
				if (segmentsValues[RouteSegments.Method]) {
					setScimMethod(segmentsValues[RouteSegments.Method] as SCIMMethod);
				}
				if (segmentsValues[RouteSegments.StepNum]) {
					setScimGuideStepNum(segmentsValues[RouteSegments.StepNum] as number);
				}
			}
			// XAA writes ONLY its own slot (never the SSO idpID/method). idp is the
			// selected XAA provider and method always Method.Xaa.
			if (guideType === GuideType.XAA) {
				if (segmentsValues[RouteSegments.IdPID]) {
					setXaaIdPID(segmentsValues[RouteSegments.IdPID] as string);
				}
				if (segmentsValues[RouteSegments.Method]) {
					setXaaMethod(segmentsValues[RouteSegments.Method] as Method);
				}
				if (segmentsValues[RouteSegments.StepNum]) {
					setXaaGuideStepNum(segmentsValues[RouteSegments.StepNum] as number);
				}
			}
		},
		[
			setGuideStepNum,
			setGuideType,
			setIdPID,
			setMethod,
			setScimGuideStepNum,
			setScimIdPID,
			setScimMethod,
			setXaaIdPID,
			setXaaMethod,
			setXaaGuideStepNum
		]
	);

	return syncIdPConfigContextToSegments;
};

export const useStepNavigate = () => {
	const navigate = useNav();

	const {
		guideType,
		idpID: currentIdPID,
		method: currentMethod,
		guideStepNum: currentGuideStepNum,
		scimMethod: currentSCIMMethod,
		scimIdpID: currentSCIMIdPID,
		scimGuideStepNum: currentSCIMGuideStepNum,
		xaaIdpID: currentXaaIdPID,
		xaaGuideStepNum: currentXaaGuideStepNum
	} = useIdPConfigContext();

	const syncIdPConfigContextToSegments = useSyncIdPConfigContextToSegments();

	const navigateToStep = useCallback(
		(stepNumber: number, idpID?: string | null, method?: Method | null) => {
			/* istanbul ignore if */
			if ((!idpID && !currentIdPID) || (!method && !currentMethod)) {
				return;
			}

			const segmentValues = {
				[RouteSegments.Guide]: GuideType.SSO,
				[RouteSegments.IdPID]: (idpID || currentIdPID)!,
				[RouteSegments.Method]: (method || currentMethod)!,
				[RouteSegments.StepNum]: stepNumber
			};

			syncIdPConfigContextToSegments(segmentValues);
			navigate(fillSegments(ROUTES.guideStep, segmentValues));
		},
		[currentIdPID, currentMethod, navigate, syncIdPConfigContextToSegments]
	);

	const navigateToScimStep = useCallback(
		(stepNumber: number, idpID?: string | null, method?: SCIMMethod | null) => {
			/* istanbul ignore if */
			if ((!idpID && !currentSCIMIdPID) || (!method && !currentSCIMMethod)) {
				return;
			}

			const segmentValues = {
				[RouteSegments.Guide]: GuideType.SCIM,
				[RouteSegments.IdPID]: (idpID || currentSCIMIdPID)!,
				[RouteSegments.Method]: (method || currentSCIMMethod)!,
				[RouteSegments.StepNum]: stepNumber
			};

			syncIdPConfigContextToSegments(segmentValues);
			navigate(fillSegments(ROUTES.guideStep, segmentValues));
		},
		[
			currentSCIMIdPID,
			currentSCIMMethod,
			navigate,
			syncIdPConfigContextToSegments
		]
	);

	const navigateToXaaStep = useCallback(
		(stepNumber: number, idpID?: string | null) => {
			const segmentValues = {
				[RouteSegments.Guide]: GuideType.XAA,
				// The XAA provider id is content-sourced (the selected catalog entry),
				// defaulting to the generic content idp like SAML/OIDC/SCIM do.
				[RouteSegments.IdPID]: idpID || currentXaaIdPID || GENERIC_IDP_ID,
				[RouteSegments.Method]: Method.Xaa,
				[RouteSegments.StepNum]: stepNumber
			};

			syncIdPConfigContextToSegments(segmentValues);
			navigate(fillSegments(ROUTES.guideStep, segmentValues));
		},
		[currentXaaIdPID, navigate, syncIdPConfigContextToSegments]
	);

	const navigateToNextStep = useCallback(() => {
		switch (guideType) {
			case GuideType.SSO:
				navigateToStep((currentGuideStepNum || 0) + 1);
				break;
			case GuideType.SCIM:
				navigateToScimStep((currentSCIMGuideStepNum || 0) + 1);
				break;
			case GuideType.XAA:
				navigateToXaaStep((currentXaaGuideStepNum || 0) + 1);
				break;
			/* istanbul ignore next: not possible  */
			default:
				break;
		}
	}, [
		currentGuideStepNum,
		currentSCIMGuideStepNum,
		currentXaaGuideStepNum,
		guideType,
		navigateToScimStep,
		navigateToStep,
		navigateToXaaStep
	]);

	return {
		navigateToStep,
		navigateToNextStep,
		navigateToScimStep,
		navigateToXaaStep
	};
};

export const useSSOConfigCardNavigate = () => {
	const { data: ssoStatus } = useSSOStatus();

	const { navigateToStep } = useStepNavigate();
	const navigate = useNav();

	const ssoConfigCardNavigate = useCallback(() => {
		if (ssoStatus?.idp && ssoStatus?.method) {
			navigateToStep(
				ssoStatus.guideStepNum || 1,
				ssoStatus.idp.id,
				ssoStatus.method
			);
		} else {
			navigate(ROUTES.selectIdP);
		}
	}, [navigate, navigateToStep, ssoStatus]);

	return ssoConfigCardNavigate;
};

export const useTestingNavigate = () => {
	const navigate = useNav();

	const { idpID: currentIdPID, method: currentMethod } = useIdPConfigContext();
	const syncIdPConfigContextToSegments = useSyncIdPConfigContextToSegments();

	const navigateToTesting = useCallback(() => {
		/* istanbul ignore if */
		if (!currentIdPID || !currentMethod) {
			return;
		}

		const segmentValues = {
			[RouteSegments.Guide]: GuideType.SSO,
			[RouteSegments.IdPID]: currentIdPID!,
			[RouteSegments.Method]: currentMethod!
		};

		syncIdPConfigContextToSegments(segmentValues);
		navigate(fillSegments(ROUTES.testing, segmentValues));
	}, [currentIdPID, currentMethod, syncIdPConfigContextToSegments, navigate]);

	return { navigateToTesting };
};

export const useDefaultMappingNavigate = (
	idpId: string | undefined | null,
	method: Method | undefined | null
) => {
	const navigate = useNav();

	const { data: methodGuideData } = useMethodGuide(idpId, method);
	const syncIdPConfigContextToSegments = useSyncIdPConfigContextToSegments();

	// -1 when the guide has no mapping step. Kept out of the returned object below so the button
	// that calls this is disabled rather than sending the user to step 0.
	const mappingStepIndex =
		methodGuideData?.guide.findIndex(
			// search, not test: MAPPING_CONTENT_COMP is a /g regex, and test advances its lastIndex,
			// so running it on every render made the result alternate between hit and miss.
			(guideStep) => guideStep.content.search(MAPPING_CONTENT_COMP) !== -1
		) ?? -1;

	const navigateToDefaultMapping = useCallback(() => {
		const segmentValues = {
			[RouteSegments.Guide]: GuideType.SSO,
			[RouteSegments.IdPID]: idpId,
			[RouteSegments.Method]: method,
			[RouteSegments.StepNum]: mappingStepIndex + 1
		};

		syncIdPConfigContextToSegments(segmentValues);
		navigate(fillSegments(ROUTES.guideStep, segmentValues));
	}, [
		idpId,
		mappingStepIndex,
		method,
		navigate,
		syncIdPConfigContextToSegments
	]);

	if (!methodGuideData || !idpId || !method || mappingStepIndex < 0) {
		return {};
	}

	return { navigateToDefaultMapping };
};

export const useDefaultTestingNavigate = (
	idpId: string | undefined | null,
	method: Method | undefined | null
) => {
	const navigate = useNav();
	const syncIdPConfigContextToSegments = useSyncIdPConfigContextToSegments();

	const navigateToDefaultTesting = useCallback(() => {
		const segmentValues = {
			[RouteSegments.Guide]: GuideType.SSO,
			[RouteSegments.IdPID]: idpId,
			[RouteSegments.Method]: method
		};
		syncIdPConfigContextToSegments(segmentValues);
		navigate(fillSegments(ROUTES.testing, segmentValues));
	}, [idpId, method, navigate, syncIdPConfigContextToSegments]);

	if (!idpId || !method) {
		return {};
	}

	return { navigateToDefaultTesting };
};

export const useNavigateToJit = () => {
	const navigate = useNav();

	const navigateToJit = useCallback(() => {
		navigate(ROUTES.jit);
	}, [navigate]);

	return { navigateToJit };
};
